import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Summary = ({ configuration, onExcelExport, onPrevious, loading }) => {
  const { poles = [], accessories, accessory_quantities, transport_option, config_code } = configuration;
  const totalMasts = poles.reduce((sum, e) => sum + (e.quantity || 1), 0);

  const [configCode, setConfigCode] = useState(config_code || null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [customer, setCustomer] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [sendStatus, setSendStatus] = useState({ state: 'idle', message: '' });
  const savedRef = useRef(false);

  // Konfiguration automatisch speichern und Code erzeugen
  useEffect(() => {
    if (config_code || savedRef.current || poles.length === 0) return;
    savedRef.current = true;
    const saveConfiguration = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/configurations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(configuration)
        });
        const data = await response.json();
        if (data.code) setConfigCode(data.code);
      } catch (error) {
        console.error('Fehler beim Speichern der Konfiguration:', error);
      }
    };
    saveConfiguration();
  }, []);

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(configCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (e) {
      console.error('Kopieren fehlgeschlagen:', e);
    }
  };

  const updateCustomer = (field, value) => {
    setCustomer(prev => ({ ...prev, [field]: value }));
    if (sendStatus.state === 'error') setSendStatus({ state: 'idle', message: '' });
  };

  const handleSendInquiry = async () => {
    if (!customer.name.trim() || !customer.email.trim()) {
      setSendStatus({ state: 'error', message: 'Bitte geben Sie mindestens Name und E-Mail-Adresse an.' });
      return;
    }
    setSendStatus({ state: 'sending', message: '' });
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ configuration, customer, config_code: configCode || '' })
      });
      const data = await response.json();
      if (response.ok) {
        setSendStatus({ state: 'success', message: 'Ihre Anfrage wurde erfolgreich an vertrieb@xortec.de gesendet. Eine Kopie geht an Ihre E-Mail-Adresse.' });
      } else {
        setSendStatus({ state: 'error', message: data.detail || 'E-Mail-Versand fehlgeschlagen. Bitte versuchen Sie es erneut.' });
      }
    } catch (error) {
      setSendStatus({ state: 'error', message: 'Verbindungsfehler. Bitte versuchen Sie es erneut.' });
    }
  };

  const formatSpecValue = (key, value) => {
    switch (key) {
      case 'type':
        return value === 'starre' ? 'Starrer Mast' : 'Kippbarer Mast';
      case 'variant':
        return value === 'konisch' ? 'Konisch' : 'Zylindrisch';
      case 'cable_type':
        return value === 'fester_zopf' ? 'Fester Zopf' : 'Loser Zopf';
      case 'mounting':
        return value === 'erdstück' ? 'Erdstück' : 'Fußplatte';
      case 'tilt_mechanism':
        return value === 'winde' ? 'Mit Winde' : 'Mit Keil';
      default:
        return value;
    }
  };

  return (
    <div className="animate-slideIn">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">
          Zusammenfassung Ihrer Konfiguration
        </h2>
        <p className="text-slate-100 max-w-2xl mx-auto drop-shadow">
          Überprüfen Sie Ihre Auswahl und exportieren Sie die Konfiguration als Excel 
          für Ihre Anfrage bei Xortec.
        </p>
      </div>

      <div className="space-y-8">
        {/* Konfigurationscode */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 rounded-xl shadow-lg p-6 text-white" data-testid="config-code-panel">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">Ihr Konfigurationscode</h3>
              <p className="text-blue-200 text-sm">
                Mit diesem Code können Sie Ihre Konfiguration jederzeit auf der Startseite wieder aufrufen oder unseren Kollegen zur Angebotserstellung weiterleiten.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className="px-4 py-2 bg-white/15 border border-white/30 rounded-lg font-mono text-xl font-bold tracking-wider"
                data-testid="config-code-value"
              >
                {configCode || 'wird erstellt…'}
              </span>
              {configCode && (
                <button
                  onClick={handleCopyCode}
                  className="px-3 py-2 bg-white text-blue-900 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
                  data-testid="config-code-copy-btn"
                >
                  {codeCopied ? '✓ Kopiert' : 'Kopieren'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Kameramast Details */}
        <div className="summary-section">
          <h3 className="summary-title">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            Ausgewählte Kameramasten
            {poles.length > 0 && (
              <span className="text-sm font-normal text-green-700 ml-2 px-2 py-1 bg-green-100 rounded-full" data-testid="summary-total-masts">
                Gesamt: {totalMasts} Stück
              </span>
            )}
          </h3>
          
          <div className="summary-content">
            {poles.length > 0 ? (
              <div className="space-y-8">
                {poles.map((entry, idx) => {
                  const pole = entry.pole;
                  return (
                    <div key={idx} className={idx > 0 ? 'pt-6 border-t border-slate-200' : ''} data-testid={`summary-pole-${idx}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">
                            Mast {idx + 1}
                          </div>
                          <h4 className="text-xl font-bold text-slate-900 mb-2">
                            {pole.name}
                          </h4>
                          <p className="text-slate-600 mb-4">
                            {pole.description}
                          </p>
                          <div className="flex items-center gap-4 mb-4">
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                              Menge: {entry.quantity || 1}
                            </span>
                          </div>
                        </div>
                        <span className={`pole-badge ${
                          pole.type === 'starre' ? 'badge-starre' : 'badge-kippbar'
                        }`}>
                          {formatSpecValue('type', pole.type)}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <div className="spec-item">
                          <span className="spec-label">Höhe</span>
                          <span className="spec-value">{pole.height}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Durchmesser</span>
                          <span className="spec-value">{pole.diameter}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Aufsatzvariante</span>
                          <span className="spec-value">{formatSpecValue('variant', pole.variant)}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Kabelführung</span>
                          <span className="spec-value">{formatSpecValue('cable_type', pole.cable_type)}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Montage</span>
                          <span className="spec-value">{formatSpecValue('mounting', pole.mounting)}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Material</span>
                          <span className="spec-value">{pole.material}</span>
                        </div>
                        {pole.tilt_mechanism && (
                          <div className="spec-item">
                            <span className="spec-label">Kippmechanismus</span>
                            <span className="spec-value">{formatSpecValue('tilt_mechanism', pole.tilt_mechanism)}</span>
                          </div>
                        )}
                        <div className="spec-item">
                          <span className="spec-label">Artikel-Nr.</span>
                          <span className="spec-value font-mono">{pole.article_number}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-500 italic">Kein Kameramast ausgewählt</p>
            )}
          </div>
        </div>

        {/* Transportkosten */}
        <div className="summary-section">
          <h3 className="summary-title">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Transportkostenpauschale
            <span className="text-sm font-normal text-red-600 ml-2">(Pflicht)</span>
          </h3>
          
          <div className="summary-content">
            {transport_option && Object.keys(transport_option).length > 0 ? (
              <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-1">
                  {transport_option.name}
                </h4>
                <p className="text-sm text-orange-800 mb-2">
                  {transport_option.description}
                </p>
                <span className="text-xs text-orange-700">
                  Art.-Nr.: {transport_option.article_number}
                </span>
              </div>
            ) : (
              <div className="text-center py-8 bg-red-50 border border-red-200 rounded-lg">
                <svg className="w-12 h-12 text-red-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className="text-red-600 font-medium">Keine Transportoption ausgewählt</p>
              </div>
            )}
          </div>
        </div>

        {/* Zubehör */}
        <div className="summary-section">
          <h3 className="summary-title">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Optionales Zubehör
            {accessories.length > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                ({accessories.length} {accessories.length === 1 ? 'Artikel' : 'Artikel'})
              </span>
            )}
          </h3>
          
          <div className="summary-content">
            {accessories.length > 0 ? (
              <div className="space-y-4">
                {accessories.map(accessory => {
                  const quantity = accessory_quantities[accessory.id] ?? 1;
                  return (
                    <div 
                      key={accessory.id}
                      className="flex items-start justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h4 className="font-semibold text-slate-900">
                            {accessory.name}
                          </h4>
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                            Menge: {quantity}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">
                          {accessory.description}
                        </p>
                        <span className="text-xs text-slate-500">
                          Art.-Nr.: {accessory.article_number}
                        </span>
                      </div>
                      <div className="ml-4">
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {accessory.type === 'fundamentanker' ? 'Fundamentanker' : 
                           accessory.type === 'schlüssel' ? 'Schlüssel' :
                           accessory.type === 'mastbox' ? 'Mastanschlusskasten' :
                           accessory.type === 'switch' ? 'Netzwerk-Switch' : accessory.type}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p className="text-slate-500">Kein Zubehör ausgewählt</p>
              </div>
            )}
          </div>
        </div>

        {/* Anfrage direkt senden */}
        <div className="summary-section" data-testid="inquiry-form-section">
          <h3 className="summary-title">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Anfrage direkt an Xortec senden
          </h3>
          <div className="summary-content">
            <p className="text-slate-600 text-sm mb-4">
              Senden Sie Ihre Konfiguration inklusive Excel-Datei direkt an <strong>vertrieb@xortec.de</strong>.
              Sie erhalten eine Kopie an Ihre E-Mail-Adresse.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={customer.name}
                  onChange={(e) => updateCustomer('name', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Max Mustermann"
                  data-testid="inquiry-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Firma</label>
                <input
                  type="text"
                  value={customer.company}
                  onChange={(e) => updateCustomer('company', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Musterfirma GmbH"
                  data-testid="inquiry-company-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">E-Mail *</label>
                <input
                  type="email"
                  value={customer.email}
                  onChange={(e) => updateCustomer('email', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="max@musterfirma.de"
                  data-testid="inquiry-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label>
                <input
                  type="tel"
                  value={customer.phone}
                  onChange={(e) => updateCustomer('phone', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+49 ..."
                  data-testid="inquiry-phone-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Nachricht</label>
                <textarea
                  value={customer.message}
                  onChange={(e) => updateCustomer('message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ihre Nachricht an den Vertrieb (optional)"
                  data-testid="inquiry-message-input"
                />
              </div>
            </div>

            {sendStatus.state === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm mb-4" data-testid="inquiry-success-message">
                ✓ {sendStatus.message}
              </div>
            )}
            {sendStatus.state === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4" data-testid="inquiry-error-message">
                ⚠ {sendStatus.message}
              </div>
            )}

            <button
              onClick={handleSendInquiry}
              disabled={sendStatus.state === 'sending' || sendStatus.state === 'success'}
              className={`btn ${sendStatus.state === 'sending' || sendStatus.state === 'success' ? 'btn-disabled' : 'btn-primary'}`}
              data-testid="send-inquiry-btn"
            >
              {sendStatus.state === 'sending' ? (
                <>
                  <div className="loading-spinner mr-2"></div>
                  Wird gesendet...
                </>
              ) : sendStatus.state === 'success' ? (
                '✓ Anfrage gesendet'
              ) : (
                'Anfrage absenden'
              )}
            </button>
          </div>
        </div>

        {/* Kontakt-Information */}
        <div className="summary-section">
          <h3 className="summary-title">
            <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21L6.96 11.01a11.013 11.013 0 006.01 6.01l1.613-3.264a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Nächste Schritte
          </h3>
          
          <div className="summary-content">
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">
                  Anfrage stellen
                </h4>
                <p className="text-blue-800 text-sm mb-3">
                  Exportieren Sie Ihre Konfiguration als Excel und senden Sie diese an Xortec 
                  für ein individuelles Angebot.
                </p>
                <div className="text-sm text-blue-700">
                  <strong>Kontakt:</strong><br />
                  Xortec GmbH<br />
                  Website: <a href="https://www.xortec.de" className="underline">www.xortec.de</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-12 pt-8 border-t border-slate-200">
        <button
          onClick={onPrevious}
          className="btn btn-secondary"
          disabled={loading}
          data-testid="summary-previous-btn"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Zurück zum Zubehör
        </button>

        <div className="flex space-x-4">
          <button
            onClick={onExcelExport}
            disabled={loading || poles.length === 0 || !transport_option || Object.keys(transport_option).length === 0}
            className="btn btn-success"
            data-testid="export-excel-btn"
          >
            {loading ? (
              <>
                <div className="loading-spinner mr-2"></div>
                Excel wird erstellt...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel exportieren
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Summary;